import type { Conversation } from '../types';

interface Props {
  conversations:   Conversation[];
  loading:         boolean;
  filter:          'all' | 'open' | 'closed';
  onFilterChange:  (f: 'all' | 'open' | 'closed') => void;
  selectedId:      string | null;
  onSelect:        (c: Conversation) => void;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const FILTERS: { id: 'all' | 'open' | 'closed'; label: string }[] = [
  { id: 'all',    label: 'All' },
  { id: 'open',   label: 'Open' },
  { id: 'closed', label: 'Closed' },
];

export default function ConversationListPanel({
  conversations, loading, filter, onFilterChange, selectedId, onSelect,
}: Props) {
  return (
    <div className="flex flex-col h-full">
      {/* Filter tabs */}
      <div className="px-4 py-3 border-b border-slate-100 flex gap-1 bg-white shrink-0">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onFilterChange(f.id)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              filter === f.id
                ? 'text-white'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
            }`}
            style={filter === f.id ? { backgroundColor: '#C8A96E' } : undefined}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-0.5 p-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-slate-100 mb-3">
              <i className="ri-chat-3-line text-slate-400 text-xl" />
            </div>
            <p className="text-slate-500 text-sm font-medium mb-1">No conversations yet</p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Conversations from your AI widget will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {conversations.map((conv) => {
              const active = conv.id === selectedId;
              const isOpen = conv.status === 'open';
              return (
                <button
                  key={conv.id}
                  type="button"
                  onClick={() => onSelect(conv)}
                  className={`w-full text-left px-4 py-3.5 transition-all cursor-pointer ${
                    active
                      ? 'bg-[#C8A96E]/8 border-r-2 border-r-[#C8A96E]'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div
                        className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 text-white text-xs font-bold"
                        style={{ backgroundColor: '#C8A96E' }}
                      >
                        {(conv.visitor_name ?? 'V')[0].toUpperCase()}
                      </div>
                      <p className="text-[#0f172a] text-xs font-semibold truncate">
                        {conv.visitor_name ?? 'Anonymous Visitor'}
                      </p>
                    </div>
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold shrink-0 ${
                        isOpen
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {conv.status}
                    </span>
                  </div>

                  <div className="pl-9">
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 mb-1 flex-wrap">
                      {conv.visitor_email && (
                        <span className="flex items-center gap-1">
                          <i className="ri-mail-line" />
                          <span className="truncate max-w-[120px]">{conv.visitor_email}</span>
                        </span>
                      )}
                      {conv.event_type && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {conv.event_type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-300">
                        {timeAgo(conv.created_at)}
                      </span>
                      {conv.lead_id && (
                        <span className="flex items-center gap-0.5 text-[9px] text-[#C8A96E] font-medium">
                          <i className="ri-user-received-line" />
                          Lead
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}